import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { auth as firebaseAuth } from '../services/firebase';
import { getNotifications, type Notification } from '../services/notificationService';
import type { Account } from '../types/account';

interface NotificationContextData {
  notifications: Notification[];
  unreadCount: number;
  setNotifications: (notifications: Notification[]) => void;
}

export const NotificationContext = createContext<NotificationContextData | undefined>(undefined);

export const NotificationProvider: React.FC<{
  children: React.ReactNode;
  user: Account | null;
}> = ({ children, user }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    if (user) {
      const unsubscribe = getNotifications(firebaseAuth, (notifications) => {
        setNotifications(notifications);
      });
      return () => {
        unsubscribe();
      };
    }
    return undefined;
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, setNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
